import { DEMO_DAY_ANALYTICS } from '@/utils/constants';
import { useCurrentUserStore } from '@/services/auth/store';
import { usePostHog } from 'posthog-js/react';

export const useDemoDayAnalytics = () => {
  const postHogProps = usePostHog();

  const captureEvent = (eventName: string, eventParams = {}) => {
    try {
      if (postHogProps?.capture) {
        const allParams = { ...eventParams };
        const userInfo = useCurrentUserStore.getState().currentUser;
        const loggedInUserUid = userInfo?.uid;
        const loggedInUserEmail = userInfo?.email;
        const loggedInUserName = userInfo?.name;
        postHogProps.capture(eventName, { ...allParams, loggedInUserUid, loggedInUserEmail, loggedInUserName });
      }
    } catch (e) {
      console.error(e);
    }
  };

  function onInvestorPendingViewPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PENDING_VIEW_PAGE_OPENED, eventParams);
  }

  function onInvestorPendingViewGoToInvestorProfileButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PENDING_VIEW_GO_TO_INVESTOR_PROFILE_BUTTON_CLICKED);
  }

  function onInvestorPendingViewAddToCalendarButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PENDING_VIEW_ADD_TO_CALENDAR_BUTTON_CLICKED);
  }

  function onAddToCalendarModalGoogleCalendarClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ADD_TO_CALENDAR_MODAL_GOOGLE_CALENDAR_CLICKED);
  }

  function onAddToCalendarModalIcalClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ADD_TO_CALENDAR_MODAL_ICAL_CLICKED);
  }

  function onInvestorPendingViewGoToDemoDayButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PENDING_VIEW_GO_TO_DEMO_DAY_BUTTON_CLICKED);
  }

  function onInvestorProfilePageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PROFILE_PAGE_OPENED, eventParams);
  }

  function onInvestorProfileEditStarted() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PROFILE_EDIT_STARTED);
  }

  function onInvestorProfileUpdated(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PROFILE_UPDATED, eventParams);
  }

  function onInvestorDrawerInputChanged(eventParams: { field: string; value: unknown }) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_DRAWER_INPUT_CHANGED, eventParams);
  }

  function onInvestorDrawerFormSaved(eventParams: { from: string; values: Record<string, unknown> }) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_DRAWER_FORM_SAVED, eventParams);
  }

  function onFounderPendingViewPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_PENDING_VIEW_PAGE_OPENED, eventParams);
  }

  function onFounderTeamFundraisingCardClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_TEAM_FUNDRAISING_CARD_CLICKED);
  }

  function onFounderEditTeamProfileButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_EDIT_TEAM_PROFILE_BUTTON_CLICKED);
  }

  function onFounderSaveTeamDetailsClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_SAVE_TEAM_DETAILS_CLICKED);
  }

  function onFounderCancelTeamDetailsClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_CANCEL_TEAM_DETAILS_CLICKED);
  }

  function onFounderDemoMaterialUploadStarted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_DEMO_MATERIAL_UPLOAD_STARTED, eventParams);
  }

  function onFounderDemoMaterialUploadSuccess(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_DEMO_MATERIAL_UPLOAD_SUCCESS, eventParams);
  }

  function onFounderDemoMaterialUploadFailed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_DEMO_MATERIAL_UPLOAD_FAILED, eventParams);
  }

  function onFounderDemoMaterialDeleted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_DEMO_MATERIAL_DELETED, eventParams);
  }

  function onFounderDemoMaterialViewed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_DEMO_MATERIAL_VIEWED, eventParams);
  }

  function onFounderPendingViewWebsiteLinkClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_FOUNDER_PENDING_VIEW_WEBSITE_LINK_CLICKED, eventParams);
  }

  function onActiveViewPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_PAGE_OPENED, eventParams);
  }

  function onActiveViewTimeOnPage(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TIME_ON_PAGE, eventParams);
  }

  function onActiveViewFiltersApplied(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_FILTERS_APPLIED, eventParams);
  }

  function onActiveViewTeamCardClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TEAM_CARD_CLICKED, eventParams);
  }

  function onConfidentialityModalSubmitted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_CONFIDENTIALITY_MODAL_SUBMITTED, eventParams);
  }

  function onConfidentialityModalClosed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_CONFIDENTIALITY_MODAL_CLOSED, eventParams);
  }

  function onActiveViewTeamPitchDeckViewed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TEAM_PITCH_DECK_VIEWED, eventParams);
  }

  function onActiveViewTeamPitchVideoViewed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TEAM_PITCH_VIDEO_VIEWED, eventParams);
  }

  function onActiveViewLikeCompanyClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_LIKE_COMPANY_CLICKED, eventParams);
  }

  function onActiveViewConnectCompanyClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_CONNECT_COMPANY_CLICKED, eventParams);
  }

  function onActiveViewInvestCompanyClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_INVEST_COMPANY_CLICKED, eventParams);
  }

  function onActiveViewReferCompanyClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_REFER_COMPANY_CLICKED, eventParams);
  }

  function onActiveViewIntroCompanyClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_INTRO_COMPANY_CLICKED, eventParams);
  }

  function onActiveViewIntroCompanyCancelClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_INTRO_COMPANY_CANCEL_CLICKED, eventParams);
  }

  function onActiveViewIntroCompanyConfirmClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_INTRO_COMPANY_CONFIRM_CLICKED, eventParams);
  }

  function onActiveViewGiveFeedbackClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_GIVE_FEEDBACK_CLICKED, eventParams);
  }

  function onActiveViewFeedbackSubmitted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_FEEDBACK_SUBMITTED, eventParams);
  }

  function onActiveViewWelcomeVideoViewed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_WELCOME_VIDEO_VIEWED, eventParams);
  }

  function onActiveViewTeamCardViewed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TEAM_CARD_VIEWED, eventParams);
  }

  function onLandingPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_PAGE_OPENED, eventParams);
  }

  function onLandingLoginButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_LOGIN_BUTTON_CLICKED);
  }

  function onLandingRequestInviteButtonClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_REQUEST_INVITE_BUTTON_CLICKED);
  }

  function onLandingTeamCardClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_TEAM_CARD_CLICKED, eventParams);
  }

  function onLandingTeamWebsiteClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_TEAM_WEBSITE_CLICKED, eventParams);
  }

  function onAccessDeniedModalShown(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACCESS_DENIED_MODAL_SHOWN, eventParams);
  }

  function onAccessDeniedUserNotWhitelistedModalShown(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACCESS_DENIED_USER_NOT_WHITELISTED_MODAL_SHOWN, eventParams);
  }

  function onAccessDeniedRequestInviteClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACCESS_DENIED_REQUEST_INVITE_CLICKED, eventParams);
  }

  function onCompletedViewPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_PAGE_OPENED, eventParams);
  }

  function onCompletedViewApplyForNextDemoDayClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_APPLY_FOR_NEXT_DEMO_DAY_CLICKED);
  }

  function onCompletedViewGiveFeedbackClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_GIVE_FEEDBACK_CLICKED);
  }

  function onCompletedViewKeepProfileUpdatedClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_KEEP_PROFILE_UPDATED_CLICKED);
  }

  function onCompletedViewFeedbackSubmitted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_FEEDBACK_SUBMITTED, eventParams);
  }

  function onCompletedViewShowMoreLogosClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_SHOW_MORE_LOGOS_CLICKED);
  }

  function onCompletedViewShowMoreTeamsClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_SHOW_MORE_TEAMS_CLICKED);
  }

  function onCompletedViewTeamCardClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_TEAM_CARD_CLICKED, eventParams);
  }

  function onCompletedViewTeamFollowed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_TEAM_FOLLOWED, eventParams);
  }

  function onCompletedViewTeamUnfollowed(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_COMPLETED_VIEW_TEAM_UNFOLLOWED, eventParams);
  }

  function onActiveViewShowMoreLogosClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_SHOW_MORE_LOGOS_CLICKED);
  }

  function onInvestorProfileAddDetailsClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PROFILE_ADD_DETAILS_CLICKED);
  }

  function onInvestorProfileNotAnInvestorClicked() {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_PROFILE_NOT_AN_INVESTOR_CLICKED);
  }

  function onInvestorCommunicationsSettingsUpdated(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_INVESTOR_COMMUNICATIONS_SETTINGS_UPDATED, eventParams);
  }

  function onDemoDayListCardClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_DEMO_DAY_LIST_CARD_CLICKED, eventParams);
  }

  function onLandingInvestorsLinkClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_INVESTORS_LINK_CLICKED, eventParams);
  }

  function onLandingInvestorProfileLinkClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_LANDING_INVESTOR_PROFILE_LINK_CLICKED, eventParams);
  }

  function onApplicationModalFieldEntered(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_APPLICATION_MODAL_FIELD_ENTERED, eventParams);
  }

  function onApplicationModalCanceled(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_APPLICATION_MODAL_CANCELED, eventParams);
  }

  function onApplicationModalSubmitted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_APPLICATION_MODAL_SUBMITTED, eventParams);
  }

  function onApplicationModalAutoSubmitted(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_APPLICATION_MODAL_AUTO_SUBMITTED, eventParams);
  }

  function onSubscribeToDemoDayClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_SUBSCRIBE_TO_DEMO_DAY_CLICKED, eventParams);
  }

  function onAccountCreatedSuccessModalContinueToLoginClicked(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACCOUNT_CREATED_SUCCESS_MODAL_CONTINUE_TO_LOGIN_CLICKED, eventParams);
  }

  function onActiveViewVideoWatchTime(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ACTIVE_VIEW_TEAM_PITCH_VIDEO_WATCH_TIME, eventParams);
  }

  function onAnalyticsReportPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ANALYTICS_REPORT_PAGE_OPENED, eventParams);
  }

  function onAnalyticsReportTimeOnPage(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_ANALYTICS_REPORT_TIME_ON_PAGE, eventParams);
  }

  function onDemoDayAnalyticsPageOpened(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_DEMO_DAY_ANALYTICS_PAGE_OPENED, eventParams);
  }

  function onDemoDayAnalyticsPageLeft(eventParams = {}) {
    captureEvent(DEMO_DAY_ANALYTICS.ON_DEMO_DAY_ANALYTICS_PAGE_LEFT, eventParams);
  }

  return {
    onLandingPageOpened,
    onLandingLoginButtonClicked,
    onLandingRequestInviteButtonClicked,
    onLandingTeamCardClicked,
    onLandingTeamWebsiteClicked,
    onAccessDeniedModalShown,
    onAccessDeniedUserNotWhitelistedModalShown,
    onAccessDeniedRequestInviteClicked,
    onInvestorPendingViewPageOpened,
    onInvestorPendingViewGoToInvestorProfileButtonClicked,
    onInvestorPendingViewAddToCalendarButtonClicked,
    onAddToCalendarModalGoogleCalendarClicked,
    onAddToCalendarModalIcalClicked,
    onInvestorPendingViewGoToDemoDayButtonClicked,
    onInvestorProfilePageOpened,
    onInvestorProfileEditStarted,
    onInvestorProfileUpdated,
    onInvestorDrawerInputChanged,
    onInvestorDrawerFormSaved,
    onFounderPendingViewPageOpened,
    onFounderTeamFundraisingCardClicked,
    onFounderEditTeamProfileButtonClicked,
    onFounderSaveTeamDetailsClicked,
    onFounderCancelTeamDetailsClicked,
    onFounderDemoMaterialUploadStarted,
    onFounderDemoMaterialUploadSuccess,
    onFounderDemoMaterialUploadFailed,
    onFounderDemoMaterialDeleted,
    onFounderDemoMaterialViewed,
    onFounderPendingViewWebsiteLinkClicked,
    onActiveViewPageOpened,
    onActiveViewTimeOnPage,
    onActiveViewFiltersApplied,
    onActiveViewTeamCardClicked,
    onActiveViewTeamCardViewed,
    onActiveViewTeamPitchDeckViewed,
    onActiveViewTeamPitchVideoViewed,
    onActiveViewLikeCompanyClicked,
    onActiveViewConnectCompanyClicked,
    onActiveViewInvestCompanyClicked,
    onActiveViewReferCompanyClicked,
    onActiveViewIntroCompanyClicked,
    onActiveViewIntroCompanyCancelClicked,
    onActiveViewIntroCompanyConfirmClicked,
    onActiveViewGiveFeedbackClicked,
    onActiveViewFeedbackSubmitted,
    onActiveViewWelcomeVideoViewed,
    onConfidentialityModalSubmitted,
    onConfidentialityModalClosed,
    onCompletedViewPageOpened,
    onCompletedViewApplyForNextDemoDayClicked,
    onCompletedViewGiveFeedbackClicked,
    onCompletedViewKeepProfileUpdatedClicked,
    onCompletedViewFeedbackSubmitted,
    onCompletedViewShowMoreLogosClicked,
    onCompletedViewShowMoreTeamsClicked,
    onCompletedViewTeamCardClicked,
    onCompletedViewTeamFollowed,
    onCompletedViewTeamUnfollowed,
    onActiveViewShowMoreLogosClicked,
    onInvestorProfileAddDetailsClicked,
    onInvestorProfileNotAnInvestorClicked,
    onInvestorCommunicationsSettingsUpdated,
    onDemoDayListCardClicked,
    onLandingInvestorsLinkClicked,
    onLandingInvestorProfileLinkClicked,
    onApplicationModalFieldEntered,
    onApplicationModalCanceled,
    onApplicationModalSubmitted,
    onApplicationModalAutoSubmitted,
    onSubscribeToDemoDayClicked,
    onAccountCreatedSuccessModalContinueToLoginClicked,
    onActiveViewVideoWatchTime,
    onAnalyticsReportPageOpened,
    onAnalyticsReportTimeOnPage,
    onDemoDayAnalyticsPageOpened,
    onDemoDayAnalyticsPageLeft,
  };
};
